import { KeyboardArrowLeft } from "@mui/icons-material";
import DownloadIcon from "@mui/icons-material/Download";
import { Backdrop, Button, Dialog } from "@mui/material";
import { Elements, useStripe } from "@stripe/react-stripe-js";
import { loadStripe, PaymentMethod } from "@stripe/stripe-js";
import cx from "classnames";
import React, { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { ReactComponent as XMark } from "assets/x-mark.svg";
import Card from "components/Payment/UpgradeFlowComponents/Card";
import PlanSelected from "components/Payment/UpgradeFlowComponents/PlanSelected";
import { STRIPE_KEY } from "constants/env";
import {
  SLAPSHOT_MY_PLAN_URL,
  SLAPSHOT_SUBSCRIPTION_URL,
} from "constants/urls";
import { Invoice, InvoiceItem, Plan } from "models/Payment";
import { Subscription } from "models/User";
import {
  MY_PLAN_INDEX,
  SELECT_PAYMENT_METHOD_INDEX,
} from "pages/UpgradePlanFlow/UpgradePlanFlow";
import { getInvoices, upgradePlan } from "services/SubscriptionService";
import { formatPrice, formatSize, formatTimestamp } from "utils/helper";

import styles from "./ReviewPurchase.module.scss";

interface ReviewPurchaseProps {
  onBack?: () => void;
  plan: Plan;
  setStep: React.Dispatch<React.SetStateAction<number>>;
  paymentMethod: PaymentMethod;
  setSelectedPaymentMethod: React.Dispatch<
    React.SetStateAction<PaymentMethod | null>
  >;
  invoiceItems: InvoiceItem[] | null;
  subscription: Subscription;
}

const ReviewPurchase: React.FC<ReviewPurchaseProps> = ({
  onBack,
  plan,
  setStep,
  paymentMethod,
  setSelectedPaymentMethod,
  invoiceItems,
  subscription,
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const stripe = useStripe();

  if (!stripe) return null;

  return (
    <>
      <div className={styles.container}>
        <div className={styles.left}>
          {onBack && (
            <div className={styles.back} onClick={onBack}>
              <KeyboardArrowLeft className={styles.arrowLeft} />
              <span>Back</span>
            </div>
          )}

          <div className={styles.heading}>Plan Summary</div>

          <div className={styles.planSummary}>
            <div className={styles.summaryHeader}>Plan Details</div>

            <div className={styles.summaryBody}>
              <table className={styles.table}>
                <tr>
                  <td className={styles.th}>Selected Plan</td>
                  <td className={styles.td}>{`${plan.name} Plan`}</td>
                </tr>

                <tr>
                  <td className={styles.th}>No. of Seats</td>
                  <td className={styles.td}>
                    {plan.price.unit_amount_decimal > 0 ? "Unlimited" : "1"}
                  </td>
                </tr>

                <tr>
                  <td className={styles.th}>Asset Storage</td>
                  <td
                    className={styles.td}
                  >{`${formatSize(plan.storage, "GB")}`}</td>
                </tr>

                <tr>
                  <td className={styles.th}>Max Resolution</td>
                  <td className={styles.td}>{plan.resolution.name}</td>
                </tr>

                <tr>
                  <td className={styles.th}>Export Frames</td>
                  <td className={styles.td}>{plan.frames}</td>
                </tr>

                <tr>
                  <td className={styles.th}>Extra Cost of Inf. Frames</td>
                  <td
                    className={styles.td}
                  >{`${formatPrice(plan.extra_frame_cost, 3)}/frame once over`}</td>
                </tr>

                <tr>
                  <td className={styles.th}>Extra Cost of Autopilot Frames</td>
                  <td
                    className={styles.td}
                  >{`${formatPrice(plan.autopilot_frame_cost, 3)}/frame once over`}</td>
                </tr>
              </table>
            </div>
          </div>
          <div className={styles.paymentSummary}>
            <div className={styles.paymentHeader}>Payment Details</div>

            <div className={styles.paymentBody}>
              <Card
                paymentMethod={paymentMethod}
                onClick={() => {}}
                onRemove={() => {
                  setSelectedPaymentMethod(null);
                  setStep(SELECT_PAYMENT_METHOD_INDEX);
                }}
                hideOptions
                subscription={subscription}
              />
            </div>
          </div>
        </div>

        <div className={styles.rightSection}>
          <PlanSelected
            subscription={subscription}
            invoiceItems={invoiceItems}
            disabled={!!subscription?.is_processing_payment}
            plan={plan}
            submitText="Confirm Purchase"
            onSubmit={() => {
              setIsProcessing(true);
              upgradePlan(subscription.id, paymentMethod.id, plan.id)
                .then(async (data) => {
                  const result = await stripe.confirmPayment({
                    clientSecret: data.payment_intent_secret,
                    confirmParams: {
                      return_url: window.location.href,
                    },
                    redirect: "if_required",
                  });

                  const { error } = result;
                  if (error) {
                    toast.error(`Payment failed: ${error.message}`);
                  } else {
                    const invoiceData = await getInvoices(
                      subscription.id,
                      data.invoice_id,
                    );
                    setInvoice(invoiceData);
                  }
                })
                .catch((error) => {
                  if (error.detail) {
                    toast.error(error.detail);
                  } else {
                    toast.error("Something went wrong");
                    console.error(error);
                  }
                })
                .finally(() => {
                  setIsProcessing(false);
                });
            }}
          />
        </div>
      </div>

      <Backdrop open={isProcessing || !!invoice}>
        {isProcessing && (
          <div>
            <img src="/loading.gif" alt="" />
          </div>
        )}

        <Dialog
          open={!!invoice}
          sx={{ ".MuiDialog-paper": { backgroundColor: "transparent" } }}
        >
          {invoice && invoice.status === "succeeded" && (
            <div className={styles.invoiceContainer}>
              <div className={styles.invoiceHeader}>
                <div>Invoice Paid</div>

                <button
                  onClick={() => {
                    setInvoice(null);
                    if (location.pathname.startsWith(SLAPSHOT_MY_PLAN_URL))
                      navigate(SLAPSHOT_MY_PLAN_URL);
                    else
                      navigate(
                        `${SLAPSHOT_SUBSCRIPTION_URL}${location.search}`,
                      );
                    setStep(MY_PLAN_INDEX);
                  }}
                  type="button"
                  className="closeButton"
                >
                  <XMark />
                </button>
              </div>

              <div className={styles.invoiceBody}>
                <div className={styles.amountDetails}>
                  <img className={styles.done} src="/done.gif" alt="" />
                  <div
                    className={styles.invoiceAmount}
                  >{`${formatPrice(invoice.price)} ${invoice.price.currency.toUpperCase()}`}</div>
                  <div className={styles.viewDetails}>
                    View invoice and payment details
                  </div>
                </div>

                <div className={styles.extraDetails}>
                  <div className={styles.extraDetailRow}>
                    <div className={styles.extraDetail}>Invoice number</div>
                    <div>{invoice.number}</div>
                  </div>
                  <div className={styles.extraDetailRow}>
                    <div className={styles.extraDetail}>Payment Date</div>
                    <div>{formatTimestamp(invoice.date)}</div>
                  </div>
                </div>
              </div>

              <div className={styles.invoiceFooter}>
                <Button
                  className={cx(
                    styles.slapShotButtonOutline,
                    styles.invoiceBtn,
                  )}
                  onClick={() => {
                    window.open(invoice.invoice_url);
                  }}
                >
                  Download Invoice
                </Button>

                {invoice.receipt_url && (
                  <Button
                    onClick={() => {
                      if (invoice.receipt_url) window.open(invoice.receipt_url);
                    }}
                    className={cx(styles.slapShotButton, styles.receiptBtn)}
                  >
                    <DownloadIcon
                      sx={{ fontSize: "16px", marginRight: "5px" }}
                    />
                    Download Receipt
                  </Button>
                )}
              </div>
            </div>
          )}
        </Dialog>
      </Backdrop>
    </>
  );
};

const ReviewPurchaseWrapper: React.FC<ReviewPurchaseProps> = (props) => {
  const stripePromise = useMemo(() => loadStripe(STRIPE_KEY), []);

  return (
    <Elements stripe={stripePromise}>
      <ReviewPurchase {...props} />
    </Elements>
  );
};

export default ReviewPurchaseWrapper;
